import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { curriculumApi, WorldWithProgress, LevelWithProgress } from '../../api/curriculumApi';
import { ArrowLeftIcon, LockClosedIcon, PlayIcon } from '@heroicons/react/24/solid';
import { CheckCircleIcon } from '@heroicons/react/24/outline';

/**
 * WorldDetailPage Component
 * 
 * Displays a skill tree view of all levels within a world.
 * Shows level nodes with connections, progress indicators, and lock states.
 */
const WorldDetailPage: React.FC = () => {
  const { worldId } = useParams<{ worldId: string }>();
  const navigate = useNavigate();
  
  const [world, setWorld] = useState<WorldWithProgress | null>(null);
  const [levels, setLevels] = useState<LevelWithProgress[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (worldId) {
      loadWorldData(parseInt(worldId));
    }
  }, [worldId]);

  const loadWorldData = async (id: number) => {
    try {
      setLoading(true);
      const [worldData, levelsData] = await Promise.all([
        curriculumApi.getWorld(id),
        curriculumApi.getLevelsForWorld(id),
      ]);
      setWorld(worldData);
      setLevels(levelsData);
    } catch (err) {
      console.error('Failed to load world data:', err);
      setError('Failed to load world. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const isLevelLocked = (level: LevelWithProgress, index: number): boolean => {
    // All levels are unlocked - users can access any content
    return false;
  };

  const getLevelStatus = (level: LevelWithProgress, index: number): 'locked' | 'in-progress' | 'completed' | 'available' => {
    const locked = isLevelLocked(level, index);
    if (locked) return 'locked';
    
    if (level.progress?.is_completed) return 'completed';
    if ((level.completed_sections || 0) > 0) return 'in-progress';
    
    return 'available';
  };

  const handleLevelClick = (level: LevelWithProgress, index: number) => {
    const status = getLevelStatus(level, index);
    if (status === 'locked') {
      return; // Don't navigate if locked
    }
    navigate(`/learn/level/${level.id}`);
  };

  const handleBackClick = () => {
    navigate('/learn');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-base-100">
        <span className="loading loading-spinner loading-lg text-primary"></span>
      </div>
    );
  }

  if (error || !world) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-base-100">
        <div className="alert alert-error max-w-md">
          <svg xmlns="http://www.w3.org/2000/svg" className="stroke-current shrink-0 h-6 w-6" fill="none" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <span>{error || 'World not found'}</span>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-base-100">
      {/* Header with Back Button */}
      <div className="bg-base-200 py-8 px-4">
        <div className="container mx-auto max-w-6xl">
          <button
            onClick={handleBackClick}
            className="btn btn-ghost btn-sm mb-4"
          >
            <ArrowLeftIcon className="h-5 w-5" />
            Back to Worlds
          </button>
          
          <div className="flex items-center gap-4">
            <div className="text-5xl">{world.icon}</div>
            <div>
              <h1 className="text-3xl md:text-4xl font-bold text-base-content">
                World {world.order}: {world.title}
              </h1>
              <p className="text-base-content/70 mt-2">
                {world.description}
              </p>
              <div className="flex gap-4 mt-4">
                <div className="badge badge-primary">{world.difficulty}</div>
                <div className="badge badge-ghost">
                  {world.completed_levels || 0}/{world.total_levels || 0} levels completed
                </div>
                <div className="badge badge-ghost">
                  {world.xp_reward} XP total
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Skill Tree */}
      <div className="container mx-auto max-w-4xl px-4 py-12">
        <div className="flex flex-col items-center gap-8">
          {levels.map((level, index) => {
            const status = getLevelStatus(level, index);
            const isLocked = status === 'locked';
            const isCompleted = status === 'completed';
            const isInProgress = status === 'in-progress';

            return (
              <div key={level.id} className="w-full flex flex-col items-center">
                {/* Connection Line (not for first level) */}
                {index > 0 && (
                  <div className={`w-1 h-12 ${
                    levels[index - 1].progress?.is_completed 
                      ? 'bg-success' 
                      : 'bg-base-300'
                  }`} />
                )}

                {/* Level Node */}
                <div
                  onClick={() => handleLevelClick(level, index)}
                  className={`
                    relative w-full max-w-md transition-all duration-200
                    ${isLocked 
                      ? 'opacity-60 cursor-not-allowed' 
                      : 'hover:scale-105 cursor-pointer'
                    }
                  `}
                >
                  <div className={`
                    card bg-base-200 shadow-lg
                    ${isCompleted ? 'border-2 border-success' : ''}
                    ${isInProgress ? 'border-2 border-warning' : ''}
                    ${!isLocked && !isCompleted && !isInProgress ? 'border-2 border-primary' : ''}
                  `}>
                    <div className="card-body p-6">
                      <div className="flex items-center gap-4">
                        {/* Level Number Circle */}
                        <div className={`
                          flex items-center justify-center w-16 h-16 rounded-full shrink-0
                          ${isLocked ? 'bg-base-300' :
                            isCompleted ? 'bg-success' :
                            isInProgress ? 'bg-warning' :
                            'bg-primary'
                          }
                        `}>
                          {isLocked ? (
                            <LockClosedIcon className="h-7 w-7 text-base-100" />
                          ) : isCompleted ? (
                            <CheckCircleIcon className="h-7 w-7 text-base-100" />
                          ) : isInProgress ? (
                            <PlayIcon className="h-7 w-7 text-base-100" />
                          ) : (
                            <span className="text-2xl font-bold text-base-100">
                              {level.order}
                            </span>
                          )}
                        </div>

                        {/* Level Info */}
                        <div className="flex-1">
                          <h3 className="text-xl font-bold text-base-content">
                            {level.title}
                          </h3>
                          <p className="text-sm text-base-content/70 mt-1">
                            {level.description}
                          </p>
                          
                          {/* Progress for in-progress levels */}
                          {isInProgress && (
                            <div className="mt-2">
                              <progress 
                                className="progress progress-warning w-full h-2"
                                value={level.completed_sections || 0}
                                max={level.total_sections || 1}
                              ></progress>
                            </div>
                          )}
                          
                          {/* Stats */}
                          <div className="flex gap-3 mt-3 text-xs">
                            <span className="text-base-content/60">
                              ⏱️ {level.estimated_minutes} min
                            </span>
                            <span className="text-base-content/60">
                              ⭐ {level.xp_reward} XP
                            </span>
                            {level.total_sections && (
                              <span className="text-base-content/60">
                                📚 {level.completed_sections || 0}/{level.total_sections} sections
                              </span>
                            )}
                          </div>
                        </div>

                        {/* Status Icon */}
                        {!isLocked && (
                          <div>
                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2.5} stroke="currentColor" className={`
                              w-6 h-6
                              ${isCompleted ? 'text-success' :
                                isInProgress ? 'text-warning' :
                                'text-primary'
                              }
                            `}>
                              <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
                            </svg>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Empty State */}
        {levels.length === 0 && (
          <div className="text-center py-20">
            <div className="text-6xl mb-4">📚</div>
            <h3 className="text-2xl font-bold mb-2">No Levels Available</h3>
            <p className="text-base-content/70">
              This world doesn't have any levels yet. Check back soon!
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default WorldDetailPage;
